/**
 * Enhanced Product Service
 * Handles enhanced product information API calls including image uploads
 */

import { api } from '../lib/axios';
import { 
  EnhancedBatchCreationResponse,
  EnhancedBatchTemplate,
  ProductInfoUpdateResponse,
  EnhancedProductDisplay
} from '../../../backend/src/types/enhanced-product-info.types';

export interface EnhancedBatchFormData {
  productName: string;
  productCode: string;
  unitCode: string;
  eventDate: string;
  serialStart: number;
  serialEnd: number;
  productType?: 'manufactured' | 'produce';
  qrTokenFormat?: 'legacy' | 'short';
  qrTokenLength?: number;
  labelDesign?: any;
  processVersion?: string;
  includeInnerQr?: boolean;
  productImages?: File[];
  nutritionInfo?: any;
  customContent?: any;
  produceDetails?: any;
  manufacturingDetails?: any;
  packagingData?: {
    unitsPerCase: number;
    casesPerPallet: number;
    caseDimensions?: string;
    palletDimensions?: string;
  };
}

const normalizeNutritionInfo = (info: any) => {
  if (!info) return info;
  const normalized: any = { ...info };
  if (normalized.servingSize !== undefined) {
    normalized.servingSize = String(normalized.servingSize).trim();
  }
  if (normalized.caloriesPerServing !== undefined) {
    const val = Number(normalized.caloriesPerServing);
    normalized.caloriesPerServing = isNaN(val) ? undefined : val;
  }
  if (normalized.caloriesPer100g !== undefined) {
    const val = Number(normalized.caloriesPer100g);
    normalized.caloriesPer100g = isNaN(val) ? undefined : val;
  }
  if (normalized.servingsPerContainer !== undefined) {
    const val = Number(normalized.servingsPerContainer);
    normalized.servingsPerContainer = isNaN(val) ? undefined : val;
  }
  if (normalized.macronutrients) {
    normalized.macronutrients = { ...normalized.macronutrients };
    ['protein','carbohydrates','totalFat','saturatedFat','transFat','fiber','sugar','sodium'].forEach(key => {
      if (normalized.macronutrients[key] !== undefined) {
        const numVal = Number(normalized.macronutrients[key]);
        normalized.macronutrients[key] = isNaN(numVal) ? undefined : numVal;
      }
    });
  }
  return normalized;
};

class EnhancedProductService {
  /**
   * Create enhanced product batch with images and additional information
   */
  async createEnhancedBatch(data: EnhancedBatchFormData): Promise<{ success: boolean; batch?: EnhancedBatchCreationResponse; error?: string }> {
    try {
      const formData = new FormData();
      
      // Add basic batch data
      formData.append('productName', data.productName);
      formData.append('productCode', data.productCode);
      formData.append('unitCode', data.unitCode);
      formData.append('eventDate', data.eventDate);
      formData.append('serialStart', data.serialStart.toString());
      formData.append('serialEnd', data.serialEnd.toString());
      formData.append('productType', data.productType || 'manufactured');
      if (data.qrTokenFormat) {
        formData.append('qrTokenFormat', data.qrTokenFormat);
      }
      if (data.qrTokenLength) {
        formData.append('qrTokenLength', data.qrTokenLength.toString());
      }
      if (data.processVersion) {
        formData.append('processVersion', data.processVersion);
      }
      if (data.includeInnerQr === false) {
        formData.append('includeInnerQr', 'false');
      }
      if (data.labelDesign) {
        formData.append('labelDesign', JSON.stringify(data.labelDesign));
      }
      
      // Add enhanced information as JSON strings
      if (data.nutritionInfo && Object.keys(data.nutritionInfo).length > 0) {
        formData.append('nutritionInfo', JSON.stringify(normalizeNutritionInfo(data.nutritionInfo)));
      }
      
      if (data.customContent && Object.keys(data.customContent).length > 0) {
        formData.append('customContent', JSON.stringify(data.customContent));
      }

      if (data.produceDetails && Object.keys(data.produceDetails).length > 0) {
        formData.append('produceDetails', JSON.stringify(data.produceDetails));
      }
      
      if (data.manufacturingDetails && Object.keys(data.manufacturingDetails).length > 0) {
        formData.append('manufacturingDetails', JSON.stringify(data.manufacturingDetails));
      }
      
      // Add packaging data
      if (data.packagingData && data.packagingData.unitsPerCase && data.packagingData.casesPerPallet) {
        formData.append('packagingData', JSON.stringify(data.packagingData));
      }
      
      // Add image files
      if (data.productImages && data.productImages.length > 0) {
        data.productImages.forEach((file) => {
          formData.append('productImages', file);
        });
      }

      const response = await api.post('/enhanced-product/batch', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return {
        success: response.data.success,
        // API returns { success, message, data: { batch, products, packaging, ... } }
        batch: response.data.data?.batch,
        error: response.data.message
      };
    } catch (error: any) {
      console.error('Enhanced batch creation failed:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to create enhanced batch'
      };
    }
  }

  /**
   * Update product information for existing batch
   */
  async updateProductInformation(
    batchId: string,
    updateType: 'nutrition' | 'custom_content' | 'manufacturing' | 'produce' | 'images' | 'label_design',
    data: any
  ): Promise<ProductInfoUpdateResponse> {
    const response = await api.put(`/enhanced-product/batch/${batchId}`, {
      updateType,
      data
    });
    return response.data.data;
  }

  /**
   * Upload a logo for thermal label design
   */
  async uploadLabelLogo(batchId: string, file: File): Promise<{ logoUrl: string; logoPath: string; fileSize: number }> {
    const formData = new FormData();
    formData.append('image', file);

    const response = await api.post(`/enhanced-product/batch/${batchId}/label-design/logo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data.data;
  }

  /**
   * Get enhanced product information for QR verification
   */
  async getEnhancedProductInfo(productId: string): Promise<EnhancedProductDisplay | null> {
    try {
      const response = await api.get(`/enhanced-product/${productId}`);
      return response.data.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Get saved batch configuration for reuse
   */
  async getBatchTemplate(batchId: string): Promise<EnhancedBatchTemplate | null> {
    try {
      const response = await api.get(`/enhanced-product/batch/${batchId}/template`);
      return response.data.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Add images to existing batch
   */
  async addImagesToBatch(batchId: string, images: File[]): Promise<void> {
    const formData = new FormData();
    images.forEach((file) => {
      formData.append('productImages', file);
    });

    await api.post(`/enhanced-product/batch/${batchId}/images`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  /**
   * Delete images from batch
   */
  async deleteImagesFromBatch(batchId: string, imageIds: string[]): Promise<void> {
    await api.delete(`/enhanced-product/batch/${batchId}/images`, {
      data: { imageIds }
    });
  }

  /**
   * Get audit trail for product information changes
   */
  async getProductInfoAuditTrail(batchId: string): Promise<any[]> {
    const response = await api.get(`/enhanced-product/batch/${batchId}/audit`);
    return response.data.data;
  }

  /**
   * Get enhanced product configuration and limits
   */
  async getEnhancedProductConfig(): Promise<any> {
    const response = await api.get('/enhanced-product/config');
    return response.data.data;
  }

  /**
   * Get version history for a batch
   */
  async getVersionHistory(batchId: string): Promise<any[]> {
    const response = await api.get(`/enhanced-product/batch/${batchId}/versions`);
    return response.data.data;
  }

  /**
   * Compare two versions of product information
   */
  async compareVersions(batchId: string, version1: number, version2: number): Promise<any> {
    const response = await api.get(`/enhanced-product/batch/${batchId}/compare`, {
      params: { version1, version2 }
    });
    return response.data.data;
  }

  /**
   * Rollback to a specific version
   */
  async rollbackToVersion(batchId: string, version: number, userId: string, reason: string): Promise<any> {
    const response = await api.post(`/enhanced-product/batch/${batchId}/rollback`, {
      version,
      userId,
      reason
    });
    return response.data.data;
  }
}

export const enhancedProductService = new EnhancedProductService();
